import React, { useCallback, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod';

import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { clearAll, showLetterEvaluationFormFunc } from '../../../config/redux/slice/appSlice'
import { Spinner } from '../../components';
import { getFileFromUrl } from '../../../helpers/helpers';
import { ROLES } from '../../../constants';
import { letterSchema } from '../../../helpers/validation';

import SendConfirmationModal from './SendConfirmationModal';
import Editor from '../../components/Editor';
import EmailList from '../../components/Form/EmailList';

export default function LetterEvaluationModal() {
    const dispatch = useDispatch();
    const [letterData, setLetterData] = useState(null);

    const { userLoading, digitalLetterManagement: { generalLetterDetails} } = useSelector((state) => state.app);
    const { userData } = useSelector((state) => state.authData)

    const getDefaultValues = useCallback(async () => {
        if (Object.keys(generalLetterDetails).length === 0) return undefined;

        return {
          attached_documents: await getFileFromUrl(generalLetterDetails?.ATTACH_DOCUMENTS),
          digital_signature: await getFileFromUrl(generalLetterDetails?.DIGITAL_SIGNATURE),
          emails_of_recipients: generalLetterDetails?.RECEIVER_EMAILS?.split(','),
          status: 'send_message',
          comments: '',
          subject: generalLetterDetails?.LETTER_SUBJECT,
          message: generalLetterDetails?.LETTER_CONTENTS,
          digital_reference_number: generalLetterDetails?.DIGITAL_LETTER_REF,
          letterType: 'inbox'
        }
    }, [generalLetterDetails]);

    const { control, register, watch, handleSubmit, setValue,  formState: {errors}, reset } = useForm({
      mode: 'onTouched',
      resolver: zodResolver(letterSchema),
      defaultValues: async () => getDefaultValues()
    });

    const attachedDocument = watch('attached_documents');
    const digitalSignature = watch('digital_signature');

    const onSubmit = useCallback(async (data) => {
      dispatch(clearAll({}));
      setLetterData({ ...data, emails_of_recipients: data.emails_of_recipients.join(',') })
    }, [dispatch]);

    const onCloseModal = useCallback(async (data) => {
      dispatch(clearAll({}));
      setLetterData(null);
    }, [dispatch]);

    return (
      <div className="modal fade show"  id="basicModal"  tabIndex="-1" aria-labelledby="exampleModalCenterTitle" aria-hidden="true" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.3)", overflowY: 'auto' }}>
        {letterData && <SendConfirmationModal is_modal is_new_letter={false} resetValues={reset} letterData={letterData} onCloseModal={onCloseModal} />}
        
          <div style={{overflowY: 'auto'}} className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div className="modal-content">
              <div className="email-right-column app email-body p-2 p-sm-3 p-md-4 p-xl-5">
                <div className="modal-header">
                  <h5 className="modal-title" id="exampleModalCenterTitle">Add Evaluation</h5>
                  <button type="button" className="close" data-dismiss="modal" aria-label="Close"onClick={() => dispatch(showLetterEvaluationFormFunc({ modal: false }))}>
                    <span aria-hidden="true">×</span>
                  </button>
                </div>

                <div className='modal-body'>
                  <form className="email-compose" onSubmit={handleSubmit(onSubmit)}>
                    <div style={{columnGap: '1.3rem'}} className='border-bottom mb-2 pb-2 d-flex align-items-center flex-wrap'>
                      <div>
                        <Controller
                          control={control}
                          name='attached_documents'
                          render={(props) => (
                            <>
                              <input 
                                id="fileUploadPaperClip" 
                                type="file" 
                                hidden 
                                onChange={(e) => props.field.onChange(e.target.files?.[0])}
                                />

                                <label style={{fontWeight: 'normal', cursor: 'pointer'}} className='mb-0' htmlFor="fileUploadPaperClip">
                                  <i style={{fontSize: '1.3rem', verticalAlign: 'middle'}} className="mdi mdi-paperclip mr-1 text-primary"></i>
                                  Attach
                                </label>
                                
                                {errors.attached_documents &&  <div className="text-danger small mt-1">{errors.attached_documents.message}</div>}
                            </>
                          )}
                        />
                      </div>

                      {[ROLES.DG.TEXT, ROLES.DIRECTOR.TEXT].includes(userData?.user_role) && (
                        <Controller
                          control={control}
                          name='digital_signature'
                          render={(props) => (
                            <>
                              <input 
                                id="fileUploadSignature" 
                                type="file" 
                                hidden 
                                onChange={(e) => props.field.onChange(e.target.files?.[0])}
                              />

                              <label style={{fontWeight: 'normal', cursor: 'pointer'}} className='mb-0' htmlFor="fileUploadSignature">
                                <i style={{fontSize: '1.3rem', verticalAlign: 'middle'}} className="mdi mdi-pencil mr-1 text-primary"></i>
                                Add Signature
                              </label>
                                
                                {errors.digital_signature &&  <div className="text-danger small mt-1">{errors.digital_signature.message}</div>}
                            </>
                          )}
                          />
                      )}
                    </div>

                    <div className="email-body-head mb-5 ">
                      <h4 className="text-dark">Edit Letter</h4>
                    </div>

                    <div>
                        <label htmlFor="emails_of_recipients">Email</label>
                        <div>
                            <Controller
                              control={control}
                              name='emails_of_recipients'
                              render={(props) => (
                                <EmailList 
                                  isEditable={userData.email === generalLetterDetails.SENDER_EMAIL}
                                  errorMessage={errors.emails_of_recipients?.message}
                                  emailList={props.field.value ?? []} 
                                  onAddEmail={(value) => props.field.onChange([...props.field.value, value])} 
                                  onRemoveEmail={value => {
                                    const newValue = props.field.value.filter((email) => email !== value);
                                    props.field.onChange(newValue);
                                  }} 
                                />
                              )}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="subject">Subject</label>

                      <input 
                        type="text" 
                        className="form-control" 
                        id="subject" 
                        placeholder="Subject "
                        {...register("subject")}
                      />

                      {errors.subject &&  <div className="text-danger small mt-1">{errors.subject.message}</div>}
                    </div>
              
                    <div>
                        <label htmlFor="comments">Comment</label>
                        <div>
                            <Controller
                              control={control}
                              name='comments'
                              render={(props) => (
                                <Editor value={props.field.value} onChange={props.field.onChange} />
                              )}
                            />

                            {errors.comments &&  <div className="text-danger small mt-1">{errors.comments.message}</div>}
                        </div>
                    </div>
                    
                    <div>
                        <label htmlFor="message">Compose letter</label>
                        <div>
                            <Controller
                              control={control}
                              name='message'
                              render={(props) => (
                                <Editor value={props.field.value} onChange={props.field.onChange} />
                              )}
                            />

                            {errors.message &&  <div className="text-danger small mt-1">{errors.message.message}</div>}
                        </div>
                    </div>
                  
                    <div style={{columnGap: '1rem'}} className="mx-1 mt-4 d-flex row rounded mb-5">
                      {attachedDocument && (
                        <div className='col-12 col-md-6 col-lg-5'>
                          <h6 className='mb-2'>Attachment</h6>
                          <div className='border d-flex justify-content-between p-2'>
                            <p className='file-name-display'>{attachedDocument.name}</p>
                            <button className='font-weight-bold' onClick={() => setValue('attached_documents', null)}>X</button>
                          </div>
                        </div>
                      )}

                      {digitalSignature &&
                        <div className='col-12 col-md-6 col-lg-5'>
                          <h6 className='mb-2'>Signature</h6>
                          <div className='border d-flex justify-content-between p-2'>
                          <p className='file-name-display'>{digitalSignature.name}</p>
                          <button className='font-weight-bold' onClick={() => setValue('digital_signature', null)}>X</button>
                        </div>
                        </div>
                      }
                    </div>
                  <div>
                  
                  {userLoading ? <Spinner/> : <button className="btn btn-primary btn-pill mb-5" type="submit">Send Letter</button>}
                </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
      </div>
  )
}
